import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save } from 'lucide-react';
import { Button } from '@components/Button';
import { Input } from '@components/Input';
import { Card, CardHeader, CardContent } from '@components/Card';
import { salesChannelService } from '@services/salesChannelService';
import { SalesChannelFormData } from '@/types/salesChannel';

export const SalesChannelFormView: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditing = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [formData, setFormData] = useState<SalesChannelFormData>({
    sales_channel_name: '',
  });

  useEffect(() => {
    if (isEditing && id) {
      loadData(parseInt(id));
    }
  }, [id, isEditing]);

  const loadData = async (itemId: number) => {
    try {
      setLoading(true);
      setError(null);
      const data = await salesChannelService.getById(itemId);
      setFormData({
        sales_channel_name: data.sales_channel_name,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const updateField = (field: keyof SalesChannelFormData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.sales_channel_name.trim()) {
      newErrors.sales_channel_name = 'Nome do canal é obrigatório';
    } else if (formData.sales_channel_name.length > 20) {
      newErrors.sales_channel_name = 'Nome deve ter no máximo 20 caracteres';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    try {
      setSaving(true);
      setError(null);

      if (isEditing && id) {
        await salesChannelService.update(parseInt(id), formData);
      } else {
        await salesChannelService.create(formData);
      }

      navigate('/settings/sales/sales-channels');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="secondary" onClick={() => navigate('/settings/sales/sales-channels')}>
            <ArrowLeft size={20} />
          </Button>
          <div>
            <div className="h-8 bg-slate-200 rounded w-48 animate-pulse" />
            <div className="h-4 bg-slate-200 rounded w-32 mt-2 animate-pulse" />
          </div>
        </div>
        <Card>
          <CardContent>
            <div className="space-y-4">
              {[...Array(1)].map((_, i) => (
                <div key={i} className="h-14 bg-slate-200 rounded animate-pulse" />
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="secondary" onClick={() => navigate('/settings/sales/sales-channels')}>
          <ArrowLeft size={20} />
        </Button>
        <div>
          <h1 className="text-h1">{isEditing ? 'Editar Canal de Venda' : 'Novo Canal de Venda'}</h1>
          <p className="text-slate-600">
            {isEditing ? 'Altere os dados do canal de venda' : 'Preencha os dados para criar um novo canal de venda'}
          </p>
        </div>
      </div>

      {/* Erro */}
      {error && (
        <Card className="bg-red-50 border-red-200">
          <CardContent>
            <p className="text-red-600">{error}</p>
          </CardContent>
        </Card>
      )}

      {/* Formulário */}
      <Card>
        <CardHeader title="Dados do Canal de Venda" />
        <CardContent>
          <div className="space-y-6">
            {/* Nome */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Nome do Canal <span className="text-red-500">*</span>
              </label>
              <Input
                value={formData.sales_channel_name}
                onChange={(e) => updateField('sales_channel_name', e.target.value)}
                placeholder="Ex: E-commerce, Loja Física, Marketplace"
                maxLength={20}
              />
              {errors.sales_channel_name && (
                <p className="text-sm text-red-500 mt-1">{errors.sales_channel_name}</p>
              )}
              <p className="text-xs text-slate-500 mt-1">{formData.sales_channel_name.length}/20 caracteres</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Ações */}
      <div className="flex justify-end gap-3">
        <Button variant="outline" onClick={() => navigate('/settings/sales/sales-channels')}>
          Cancelar
        </Button>
        <Button
          onClick={handleSave}
          loading={saving}
          icon={<Save size={18} />}
        >
          {isEditing ? 'Salvar Alterações' : 'Criar Canal'}
        </Button>
      </div>
    </div>
  );
};
